import type { AuditCategory } from '@shared/api/contracts';

export interface AuditEventCatalogItem {
  eventType: string;
  category: AuditCategory;
  reserved?: boolean;
}

const active = (category: AuditCategory, eventTypes: string[]): AuditEventCatalogItem[] =>
  eventTypes.map((eventType) => ({ eventType, category }));

const reserved = (category: AuditCategory, eventTypes: string[]): AuditEventCatalogItem[] =>
  eventTypes.map((eventType) => ({ eventType, category, reserved: true }));

export const AUDIT_EVENT_CATALOG: AuditEventCatalogItem[] = [
  ...active('auth', [
    'auth.login.succeeded',
    'auth.login.failed',
    'auth.2fa.enabled',
    'auth.2fa.disabled',
    'auth.password_reset.requested',
    'auth.password_reset.completed',
    'auth.password_reset.initiated_by_admin',
    'auth.password.changed',
    'auth.sessions.terminated',
    'user.banned',
    'user.unbanned',
    'project.ip_allowlist.changed',
    'api.request.denied_ip',
  ]),
  ...active('users', [
    'user.created',
    'user.registered',
    'user.deleted',
    'user.restored',
    'project.created',
    'project.deleted',
    'project.member_added',
  ]),
  ...active('api', [
    'api_key.created',
    'api_key.rotated',
    'api_key.revoked',
    'project.webhook_url_changed',
    'project.settings_changed',
  ]),
  ...active('invoices', [
    'invoice.created',
    'invoice.status_changed',
    'invoice.underpaid_recalculated',
    'transaction.detected',
    'transaction.linked_to_invoice',
    'transaction.invoice_link_changed',
    'refund.created',
    'refund.status_changed',
    'refund.address_replaced',
    'refund.forced',
    'refund.rejected',
    'refund.retried',
    'refund.commented',
  ]),
  ...active('balances', [
    'withdrawal.created',
    'withdrawal.approved',
    'withdrawal.rejected',
    'withdrawal.cancelled',
    'withdrawal.status_changed',
    'auto_withdrawal.rules_changed',
    'project.commission_changed',
    'project.autoconvert_changed',
    'balance.manual_adjustment',
    'balance.withdrawals_locked',
    'address.control_mode_changed',
  ]),
  ...active('aml', [
    'aml.check_completed',
    'aml.check_skipped_technical',
    'aml.risk_level_changed',
    'aml.manual_decision',
    'aml.operation_blocked',
    'aml.monitoring_changed',
  ]),
  ...active('system', ['system.manual_review_required', 'system.db_change', 'audit.exported']),
  ...reserved('users', [
    'user.role_changed',
    'project.invitation_created',
    'project.invitation_accepted',
    'project.invitation_revoked',
    'project.member_removed',
    'project.member_permissions_changed',
  ]),
  ...reserved('api', ['approval_policy.changed', 'api_key.scopes_changed', 'webhook.resent']),
  ...reserved('balances', [
    'auto_withdrawal.address_verified',
    'balance.hold_placed',
    'balance.hold_released',
  ]),
  ...reserved('aml', ['aml.operation_released']),
];

export const AUDIT_CATEGORIES: AuditCategory[] = [
  'auth',
  'users',
  'api',
  'invoices',
  'balances',
  'aml',
  'system',
];

export const AUDIT_OUTCOMES = ['SUCCESS', 'FAILURE', 'DENIED'] as const;

export const AUDIT_ACTOR_TYPES = [
  'USER',
  'ADMIN',
  'API_KEY',
  'INTERNAL_API',
  'SYSTEM',
  'CLI',
  'DB_TRIGGER',
  'ANONYMOUS',
] as const;

export const AUDIT_TARGET_TYPES = [
  'invoice',
  'withdrawal',
  'user',
  'project',
  'wallet',
  'address',
  'refund',
  'aml_check',
  'transaction',
  'audit',
] as const;

export const filterEventOptions = (categories: string[] = []): AuditEventCatalogItem[] => {
  const activeItems = AUDIT_EVENT_CATALOG.filter((item) => !item.reserved);
  if (!categories.length) return activeItems;
  return activeItems.filter((item) => categories.includes(item.category));
};

export const categoryForEventType = (eventType: string): AuditCategory | null =>
  AUDIT_EVENT_CATALOG.find((item) => item.eventType === eventType)?.category ?? null;
